/**
 * 방문 통계(Google Analytics 4).
 *
 * **라이브에서만 켠다.** 로컬·미리보기에서 돌아다닌 것이 섞이면 숫자가
 * 거짓이 된다. 측정 ID는 어차피 브라우저로 나가는 **공개 값**이라 저장소에
 * 그대로 둔다 — 비밀이 아니므로 SSM에 숨길 이유가 없다.
 *
 * ⚠️ SPA라 **페이지 이동이 새 요청이 아니다.** 그냥 붙이면 첫 화면 하나만
 * 기록되고 그 뒤로 어디를 보든 통계에 없다. 그래서 자동 집계(`send_page_view`)를
 * 끄고 주소가 바뀔 때마다 직접 보낸다 — 켜 둔 채로 직접도 보내면 한 번 본
 * 화면이 두 번 세어진다.
 */

const MEASUREMENT_ID = 'G-S0F3N0NWEX';

/** 통계를 켜는 곳. 여기 없는 주소(로컬·미리보기)에서는 아무것도 안 보낸다 */
const LIVE_HOSTS = ['life-timeline.com', 'www.life-timeline.com'];

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let started = false;

function isLive(): boolean {
  return typeof window !== 'undefined' && LIVE_HOSTS.includes(window.location.hostname);
}

/**
 * gtag를 한 번만 올린다.
 *
 * `<head>`에 직접 박지 않는 이유: 라이브인지 아닌지를 **실행할 때** 판단해야
 * 하는데, 정적 HTML은 모든 환경에 같은 것이 나간다.
 */
export function startAnalytics(): void {
  if (started || !isLive()) {
    return;
  }
  started = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  /*
   * ⚠️ **`arguments`를 그대로 넣어야 한다.** 보기에는 배열을 넣는 것과 같지만
   * gtag.js는 `dataLayer`에 쌓인 것 중 `arguments` 객체만 명령으로 읽고 진짜
   * 배열은 남의 데이터로 보고 지나친다. 처음에 나머지 매개변수(`...args`)로
   * 썼더니 `dataLayer`에는 `['event','page_view',{...}]`가 멀쩡히 쌓이는데
   * `/g/collect` 요청은 한 번도 안 나갔다 — 보내는 쪽에서는 아무 티가 안 난다.
   */
  function push(): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  }
  // 받는 인자는 부르는 쪽마다 다르다 — 넘기는 일만 하므로 타입만 씌운다
  const gtag = push as Gtag;

  window.gtag = gtag;
  gtag('js', new Date());
  // 화면 이동은 아래에서 직접 보낸다 — 여기서도 보내면 두 번 세어진다
  gtag('config', MEASUREMENT_ID, { send_page_view: false });
}

/**
 * 화면 하나를 봤다고 알린다.
 *
 * ⚠️ 주소에서 **쿼리를 떼고** 보낸다. `?tab=picks` 같은 것이야 상관없지만
 * 로그인 흐름에 토큰이 실려 오는 주소가 있어, 통째로 보내면 그게 통계
 * 서버에 남는다. 경로만으로도 "어느 화면을 봤나"는 다 나온다.
 */
export function trackPageView(path: string, title: string): void {
  if (!isLive()) {
    return;
  }
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: title,
  });
}
